import React, { useState, useEffect } from "react";
import CommonSlider from "../../../common/CommonSlider";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  isFieldError,
  isHealthCareError,
  isValidExpiryDate,
  isVersionCodeError,
} from "../../../../helpers/input-validations";
import CustomInput from "../../../common/CustomInput";
import Button from "../../../common/Button";
import { useDispatch } from "react-redux";
import { createPatientProvincialInsuranceDetails, updatePatientProvincialInsuranceDetails } from "../../../../redux/modules/patient/actions";
import {
  extractPhoneNumber,
  formatToYYYYMMDDBackend
} from "../../../../helpers/functions";
import { stateOptions, provincialInsuranceOptions } from "../../create/options";
import ConfirmationModalNew from "../../../common/ConfirmationModalNew";

const ProvincialCreate = ({ open, onClose, patient }) => {
  patient;

  const dispatch = useDispatch();
  // deep cloning array to modify behaviour
  const provinceOptions = stateOptions.map(state => { return { ...state, disabled: true } });
  const britishColm = provinceOptions.find(state => state.label === "British Columbia");
  if (britishColm)
    britishColm.disabled = false;

  const ontario = provinceOptions.find(state => state.label === "Ontario");
  if (ontario)
    ontario.disabled = false;

  const [healthCardNumber, setHealthCardNumber] = useState(
    patient.provincialInsurance?.cardNumber ?? ""
  );
  const [healthCardVersionCode, setHealthCardVersionCode] = useState(
    patient.provincialInsurance?.versionCode.toUpperCase() ?? ""
  );
  const [healthCardExpiryDate, setHealthCardExpiryDate] = useState(
    patient.provincialInsurance?.expiryDate ?? ""
  );
  const [healthCardProvince, setHealthCardProvince] = useState(patient.provincialInsurance?.province ?? "");
  const [healthCardErrors, setHealthCardErrors] = useState("");
  const [provinceErrors, setProvinceErrors] = useState("")
  const [versionCodeErrors, setVersionCodeErrors] = useState("");
  const [expiryDateErrors, setExpiryDateErrors] = useState("");
  const [provincialInsurance, setProvincialInsurance] = useState("provincialInsurance");
  const [provincialFieldDisabled, setProvincialFieldDisabled] = useState(
    false
  );
  const [openEndServiceModal, setOpenEndServiceModal] = useState(false);

  useEffect(() => {
    if (provincialInsurance === "noInsuranceNoConcent") {
      setOpenEndServiceModal(true);
    }
    setProvincialFieldDisabled(provincialInsurance !== "provincialInsurance");
  }, [provincialInsurance]);


  const manageStep = () => {
    // Skip validation and save if "noInsurancePaymentUnknown" is selected
    if (provincialInsurance === "noInsurancePaymentUnknown") {
      onClose(false);
      return;
    }

    const healthCardError = isHealthCareError(healthCardNumber);
    const provinceError = isFieldError(healthCardProvince)
    const versionCodeError = isFieldError(healthCardVersionCode);
    const expiryDateError = !isValidExpiryDate(healthCardExpiryDate);
    let validated = true;
    if (healthCardError || versionCodeError || expiryDateError || provinceError) {
      setHealthCardErrors(healthCardError);
      setVersionCodeErrors(versionCodeError);
      setExpiryDateErrors(expiryDateError);
      setProvinceErrors(provinceError)
      validated = false;
    }
    if (validated) {
      if (patient.provincialInsurance !== null) {
        dispatch(
          updatePatientProvincialInsuranceDetails(
            patient.provincialInsurance.id,
            {
              cardNumber: extractPhoneNumber(healthCardNumber),
              expiryDate: formatToYYYYMMDDBackend(healthCardExpiryDate),
              versionCode: healthCardVersionCode,
              province: healthCardProvince
            }
          )
        );
      } else {
        dispatch(
          createPatientProvincialInsuranceDetails(
            patient.id,
            {
              cardNumber: extractPhoneNumber(healthCardNumber),
              expiryDate: formatToYYYYMMDDBackend(healthCardExpiryDate),
              versionCode: healthCardVersionCode,
              province: healthCardProvince
            }
          )
        );
      }
      onClose(false);
    }
  };

  useEffect(() => {
    setHealthCardNumber(patient.provincialInsurance?.cardNumber ?? "");
    setHealthCardVersionCode(patient.provincialInsurance?.versionCode.toUpperCase() ?? "");
    setHealthCardExpiryDate(patient.provincialInsurance?.expiryDate ?? "");
    setHealthCardProvince(patient.provincialInsurance?.province ?? "");
  }, [patient]);

  return (
    <CommonSlider  open={open} onClose={onClose}>
      <ConfirmationModalNew
        open={openEndServiceModal}
        title={"No Provincial Insurance"}
        type={"primary"}
        confirmText={"Choose to Pay"}
        cancelText="Cancel & Reselect"
        content={() => (
          <p>Patient does not consent to pay for Clinical Service.  Refer to alternate healthcare provider.
          </p>
        )}
        onCross={() => {
          setOpenEndServiceModal(false);
        }}
        handleClose={() => {
          setOpenEndServiceModal(false);
        }}
        confirmAction={() => {
          setProvincialInsurance("noInsuranceConcent");
          setOpenEndServiceModal(false);
        }}
      />
      <div className="flex flex-col flex-1 h-full overflow-y-auto ">
        <div className="relative flex flex-col flex-1">
          {/* Header */}
          <div className="px-8 py-6 bg-gray-20">
            <div className="flex items-start justify-between space-x-3">
              <div className="flex flex-col">
                <p className="font-bold text-gray-800 text-display-xs">
                  {!patient.provincialInsurance ? "Add" : "Edit"} Provincial Insurance
                </p>
                <p className="text-body-md text-gray-700">{patient.lastName}, {patient.firstName}</p>
              </div>
              <div className="flex items-center">
                <button
                  type="button"
                  className="flex items-center justify-center p-1 transition-all rounded-full max-w-8 cursor-pointer right-4 top-3 bg-blue-200 hover:bg-teal-600 hover:text-blue-200 text-teal-600"
                  onClick={() => {
                    if (openEndServiceModal) {
                      setOpenEndServiceModal(false);
                    } else {
                      onClose(false);
                    }
                  }}
                >
                  <span className="sr-only">Close panel</span>
                  <XMarkIcon
                    className="w-5 h-5 m-auto"
                    strokeWidth="2"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          </div>
          <div className="flex flex-col justify-between flex-1 px-8 mt-4">
            <div className="grid grid-cols-12 gap-3">
              <p className="col-span-12 font-medium text-gray-900 text-body-md">
                Provincial Insurance
              </p>
              <CustomInput
                vertical
                className="col-span-12"
                options={provincialInsuranceOptions}
                onChange={(e) => setProvincialInsurance(e.target.value)}
                type="radio"
                name="statusOptions"
                value={provincialInsurance}
              />
              <CustomInput
                name="healthCard"
                className="col-span-6"
                label="Health Card"
                mask="9999-999-999"
                placeholder="XXXX-XXX-XXX"
                type="text"
                value={healthCardNumber}
                error={healthCardErrors}
                onChange={(e) => {
                  setHealthCardNumber(e.target.value);
                  setHealthCardErrors(isHealthCareError(e.target.value));
                }}
                disabled={provincialFieldDisabled}
                required
              />
              <CustomInput
                name="versionCode"
                className="col-span-6"
                label="Version Code"
                placeholder="ER"
                type="versionCode"
                value={healthCardVersionCode}
                onChange={(e) => {
                  setHealthCardVersionCode(e.target.value.toUpperCase());
                  setVersionCodeErrors(isVersionCodeError(e.target.value.toUpperCase())); // Clear previous error
                }}
                required
                disabled={provincialFieldDisabled}
                error={versionCodeErrors}
              />
              <CustomInput
                name="expiryDate"
                className="col-span-6"
                label="Expiry Date"
                type="date"
                value={healthCardExpiryDate}
                onChange={(e) => {
                  setHealthCardExpiryDate(e);
                  setExpiryDateErrors(!isValidExpiryDate(e));
                }}
                error={expiryDateErrors}
                disabled={provincialFieldDisabled}
              />
              <CustomInput
                options={provinceOptions}
                name="state"
                value={healthCardProvince}
                className="col-span-6"
                label="Province"
                type="dropdown"
                required
                error={provinceErrors}
                disabled={provincialFieldDisabled}
                onChange={(e) => { setProvinceErrors(isFieldError(e.value)); setHealthCardProvince(e.value) }}
              />
            </div>
            <div className="flex items-center justify-center py-6">
              <div className="flex items-center justify-center space-x-5">
                <Button
                  className="min-w-[200px]"
                  text={`Cancel`}
                  type="secondary"
                  onClick={() => onClose(false)}
                />
                <Button
                  className="min-w-[200px]"
                  text={`${!patient.provincialInsurance ? "Save" : "Update"}`}
                  type="primary"
                  onClick={() => manageStep()}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </CommonSlider>
  );
};

export default ProvincialCreate;
